/**
 * Adapter from the backend's `aggregate_table` artifact to the
 * `{ columns, rows }` shape `TableArtifact` renders.
 *
 * `aggregate_metric_grouped` has emitted `aggregate_table` since it was written,
 * and nothing has ever rendered it: `TableArtifact.isTableShape` requires a
 * `columns` array, the artifact has none, so every grouped aggregation the user
 * asked for came out as "This result can't be shown as a table." The data was
 * there the whole time — only the translation was missing.
 *
 * An adapter rather than a second table component, so there is one table
 * renderer with one set of styles, and a fix to either benefits both.
 *
 * Two shapes come back from that tool:
 *   1D — rows of `{bucket, value, unit, n_cells}`   (group_by only)
 *   2D — rows of `{bucket, per_series, total}`      (group_by × split_by)
 * They are different enough that guessing between them would be fragile, so the
 * discriminator is explicit: 2D carries `series_keys`.
 */

/** True for an artifact this adapter can convert. */
export function isAggregateTable(artifact) {
  return !!artifact && artifact.artifact_type === 'aggregate_table' && Array.isArray(artifact.rows)
}

function formatNumber(value) {
  if (value == null) return null
  if (typeof value !== 'number' || !Number.isFinite(value)) return String(value)
  // Two significant-ish decimals for small magnitudes, none for large: an
  // aggregate over Wh/kg wants "248", one over a loss percentage wants "3.14".
  const abs = Math.abs(value)
  if (abs >= 100) return value.toFixed(0)
  if (abs >= 1) return value.toFixed(2)
  return value.toPrecision(3)
}

/**
 * @param {object} artifact — an `aggregate_table` payload
 * @returns {{columns: Array, rows: Array, caption: string}|null}
 */
export function aggregateTableToTableShape(artifact) {
  if (!isAggregateTable(artifact)) return null

  const groupLabel = humanise(artifact.group_by)
  const metricLabel = artifact.metric?.label || 'Value'
  const unit = artifact.metric?.unit || ''
  // The unit belongs in the HEADER, not repeated in every cell: the tool emits
  // one unit for the whole table, and a column of "248 Wh/kg / 251 Wh/kg / …"
  // is harder to scan than a column of numbers under one labelled heading.
  const valueHeader = unit ? `${metricLabel} (${unit})` : metricLabel

  if (Array.isArray(artifact.series_keys)) {
    // 2D: one column per series, plus the row total.
    const columns = [
      { key: 'bucket', label: groupLabel },
      ...artifact.series_keys.map((key) => ({ key: `s_${key}`, label: String(key) })),
      { key: 'total', label: 'Total' },
    ]
    const rows = artifact.rows.map((row) => {
      const out = { bucket: row.bucket, total: formatNumber(row.total) }
      for (const key of artifact.series_keys) {
        // `per_series` is keyed by the series value; a bucket with no cells for
        // a series has no key at all, which must render as an em dash rather
        // than as zero — "no cells here" is not "an aggregate of zero".
        const cell = row.per_series?.[key]
        out[`s_${key}`] = cell == null ? null : formatNumber(cell.value ?? cell)
      }
      return out
    })
    return {
      columns,
      rows,
      caption: `${valueHeader} by ${groupLabel} × ${humanise(artifact.split_by)}`,
    }
  }

  // 1D: bucket, value, and how many cells went into it. `n_cells` is not
  // decoration — a mean over two cells and a mean over ninety look identical
  // without it, and the difference decides whether the number means anything.
  const columns = [
    { key: 'bucket', label: groupLabel },
    { key: 'value', label: valueHeader },
    { key: 'n_cells', label: 'Cells' },
  ]
  const rows = artifact.rows.map((row) => ({
    bucket: row.bucket,
    value: formatNumber(row.value),
    n_cells: row.n_cells,
  }))
  return { columns, rows, caption: `${valueHeader} by ${groupLabel}` }
}

/** "form_factor" / "form-factor" → "Form factor". */
function humanise(key) {
  if (!key) return 'Group'
  const spaced = String(key).replace(/[_-]+/g, ' ').trim()
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}
